import React, { useEffect, useMemo, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { Box, Tooltip, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import {
    MaterialReactTable,
    useMaterialReactTable,
    MRT_TopToolbar,
    MRT_ToggleGlobalFilterButton,
    MRT_ToggleFiltersButton,
    MRT_ToggleDensePaddingButton,
    MRT_ToggleFullScreenButton,
    type MRT_ColumnDef,
    type MRT_TableInstance,
} from "material-react-table";
import { fetchPnlSummaryView3, PnlRow, takeDailyPnlSnapshot, updateDailyChangeCloud2 } from "./Pnlsummaryview3.actions";
import { toast } from "react-toastify";
import FilterField from "../Trading/Filters/FilterField";
import ActionButton from "../../Components/Buttons/DefaultStyle/ActionButton";
import PnlSnapshotHistoryTable from "./PnlSnapshotHistoryTable";

type FilterValue = { values: string[]; isAll: boolean; isPartialAll?: boolean };

const EMPTY_FILTERS: { book: FilterValue; strategy: FilterValue } = {
    book: { values: [], isAll: false, isPartialAll: false },
    strategy: { values: [], isAll: false, isPartialAll: false },
};

export const NUMERIC_FIELDS = [
    "cost",
    "optimisation_margin",
    "daily_change",
    "pnl",
    "markup_pnl",
    "commercial_pnl",
    "jv_total_pnl",
    "jv_partner_pnl",
    "cgm_pnl",
    "net_pnl",
] as const;

const NUMERIC_FIELD_SET: Set<string> = new Set(NUMERIC_FIELDS);

export const formatNumber = (value: number) =>
    (value ?? 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

export const NumericCell = ({ cell }: { cell: any }) => {
    const value = (cell.getValue() as number) ?? 0;
    return (
        <span
            style={{
                color: value < 0 ? "#d32f2f" : "inherit",
                fontWeight: 600,
            }}
        >
            {formatNumber(value)}
        </span>
    );
};


export type RowType = "group" | "base" | "cost_adjustment" | "adjusted";

export type PnlDisplayRow = PnlRow & {
    id: string;
    rowType: RowType;
    subRows?: PnlDisplayRow[];
};

const buildRowTree = (rows: PnlRow[]): PnlDisplayRow[] =>
    rows.map((r: any, idx) => {
        const id = `${r.book}-${r.strategy}-${idx}`;

        const baseRow: PnlDisplayRow = {
            ...r,
            id: `${id}-base`,
            strategy: "Base (System)",
            rowType: "base",
            daily_change: 0,
        };

        const costMgmtRow: PnlDisplayRow = {
            ...r,
            id: `${id}-cost-mgmt`,
            strategy: "Cost Management Adjustment",
            rowType: "cost_adjustment",
            pnl: r.cost_management_adjusted_Gross_pnl ?? 0,
            markup_pnl: r.cost_management_adjusted_Markup_pnl ?? 0, 
            optimisation_margin: r.cost_management_adjusted_Optimisation_pnl ?? 0,
            commercial_pnl: r.cost_management_adjusted_Commercial_pnl ?? 0,
            jv_total_pnl: r.cost_management_adjusted_JV_Total_pnl ?? 0,
            jv_partner_pnl: r.cost_management_adjusted_JV_Partner_pnl ?? 0,
            cgm_pnl: r.cost_management_adjusted_CGM_pnl ?? 0,
            cost: r.cost_management_adjusted_Cost ?? 0,  
            net_pnl: r.cost_management_adjusted_Net_pnl ?? 0,
            daily_change: 0,
        };

        const adjustedRow: PnlDisplayRow = {
            ...r,
            id: `${id}-adjusted`,
            strategy: "Adjusted",
            rowType: "adjusted",
            pnl: r.adjusted_pnl ?? r.pnl,
            markup_pnl: r.adjusted_jv_markup_pnl ?? r.markup_pnl,
            optimisation_margin: r.adjusted_optimisation_margin ?? r.optimisation_margin,
            commercial_pnl: r.adjusted_commercial_pnl ?? r.commercial_pnl,
            jv_total_pnl: r.adjusted_jv_total_pnl ?? r.jv_total_pnl,
            jv_partner_pnl: r.adjusted_jv_partner_pnl ?? r.jv_partner_pnl,
            cgm_pnl: r.adjusted_cgm_pnl ?? r.cgm_pnl,
            cost: r.adjusted_cost ?? r.cost, 
            net_pnl: r.adjusted_net_pnl ?? r.net_pnl,
            daily_change: 0,
        };

        return {
            ...r,
            id,
            rowType: "group",
            pnl: r.adjusted_pnl ?? r.pnl,
            markup_pnl: r.adjusted_jv_markup_pnl ?? r.markup_pnl,
            optimisation_margin: r.adjusted_optimisation_margin ?? r.optimisation_margin,
            commercial_pnl: r.adjusted_commercial_pnl ?? r.commercial_pnl,
            jv_total_pnl: r.adjusted_jv_total_pnl ?? r.jv_total_pnl,
            jv_partner_pnl: r.adjusted_jv_partner_pnl ?? r.jv_partner_pnl,
            cgm_pnl: r.adjusted_cgm_pnl ?? r.cgm_pnl,
            cost: r.adjusted_cost ?? r.cost, 
            net_pnl: r.adjusted_net_pnl ?? r.net_pnl,
            subRows: [baseRow, costMgmtRow, adjustedRow],
        } as PnlDisplayRow;
    });

export const TotalsBar = <TData extends Record<string, any>,>({
    table,
    totals,
    innerRef,
}: {
    table: MRT_TableInstance<TData>;
    totals: Record<string, number>;
    innerRef: React.RefObject<HTMLDivElement>;
}) => {
    const leafColumns = table.getVisibleLeafColumns();

    return (
        <Box
            sx={{
                overflow: "hidden",
                backgroundColor: "#eef2f7",
                borderTop: "2px solid #b0bec5",
                borderBottom: "2px solid #b0bec5",
            }}
        >
            <Box
                ref={innerRef}
                sx={{
                    display: "flex",
                    width: "max-content",
                    willChange: "transform",
                }}
            >
                {leafColumns.map(column => {
                    const size = column.getSize();
                    const id = column.id;
                    const isBook = id === "book";
                    const isNumeric = NUMERIC_FIELD_SET.has(id);
                    const value = totals[id] ?? 0;

                    return (
                        <Box
                            key={id}
                            sx={{
                                width: size,
                                minWidth: size,
                                maxWidth: size,
                                flexShrink: 0,
                                padding: "4px 8px",
                                borderRight: "1px solid #dcdcdc",
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {isBook && (
                                <span
                                    style={{
                                        fontWeight: 700,
                                        fontSize: "0.78rem",
                                        color: "#2c3e50",
                                        letterSpacing: "0.02em",
                                    }}
                                >
                                    TOTAL
                                </span>
                            )}
                            {isNumeric && (
                                <Box
                                    component="span"
                                    sx={{
                                        backgroundColor: value < 0 ? "#f8d7da" : "#c8e6c9",
                                        color: value < 0 ? "#842029" : "#1b5e20",
                                        padding: "3px 12px",
                                        borderRadius: "4px",
                                        fontWeight: 700,
                                        fontSize: "0.75rem",
                                        whiteSpace: "nowrap",
                                        display: "inline-block",
                                    }}
                                >
                                    {formatNumber(value)}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

const PnlSummaryView3 = () => {
    const [rows, setRows] = useState<PnlRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);

    const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
    const [snapshotting, setSnapshotting] = useState(false);

    const [snapshotRefreshKey, setSnapshotRefreshKey] = useState(0);

    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const totalsInnerRef = useRef<HTMLDivElement | null>(null);

    const [draftFilters, setDraftFilters] = useState<{
        book: FilterValue;
        strategy: FilterValue;
    }>(EMPTY_FILTERS);

    const [appliedFilters, setAppliedFilters] = useState<{
        book: FilterValue;
        strategy: FilterValue;
    }>(EMPTY_FILTERS);

    const handleFilterChange = (
        field: string,
        values: string[],
        isAll?: boolean,
        isPartialAll?: boolean
    ) => {
        setDraftFilters(prev => ({
            ...prev,
            [field]: { values, isAll: !!isAll, isPartialAll: !!isPartialAll },
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters(draftFilters);
    };

    const handleClearAllFilters = () => {
        setDraftFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
    };

    const hasPendingChanges =
        JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters);

    const hasAnyDraftSelection =
        draftFilters.book.values.length > 0 || draftFilters.strategy.values.length > 0;

    const hasAnyAppliedSelection =
        appliedFilters.book.values.length > 0 || appliedFilters.strategy.values.length > 0;

    const isApplyDisabled = !hasPendingChanges;
    const isClearAllDisabled = !hasAnyDraftSelection && !hasAnyAppliedSelection;

    const bookOptions = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.book)))
                .filter(Boolean)
                .map(b => ({ label: b, value: b })),
        [rows]
    );

    const strategyOptions = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.strategy)))
                .filter(Boolean)
                .map(s => ({ label: s, value: s })),
        [rows]
    );

    const filteredRows = useMemo(() => {
        return rows.filter(r => {
            const bookOk =
                appliedFilters.book.values.length === 0 ||
                appliedFilters.book.values.includes(r.book);
            const stratOk =
                appliedFilters.strategy.values.length === 0 ||
                appliedFilters.strategy.values.includes(r.strategy);
            return bookOk && stratOk;
        });
    }, [rows, appliedFilters]);

    const tableData = useMemo(() => buildRowTree(filteredRows), [filteredRows]);

    const totals = useMemo<Record<string, number>>(() => {
        return NUMERIC_FIELDS.reduce((acc, field) => {
            acc[field] = tableData.reduce(
                (sum, row) => sum + (Number((row as any)[field]) || 0),
                0
            );
            return acc;
        }, {} as Record<string, number>);
    }, [tableData]);


    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchPnlSummaryView3();
            setRows(data);
        } catch (err) {
            console.error(err);
            setError("Failed to load P&L summary data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await fetchPnlSummaryView3();
                if (isMounted) setRows(data);
            } catch (err) {
                console.error(err);
                if (isMounted) setError("Failed to load P&L summary data");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (tableContainerRef.current && totalsInnerRef.current) {
            const scrollLeft = tableContainerRef.current.scrollLeft;
            totalsInnerRef.current.style.transform = `translateX(-${scrollLeft}px)`;
        }
    }, [tableData]);

    const snapshotDateStr = () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);

        const day = d.getDay();
        if (day === 0) d.setDate(d.getDate() - 2);
        if (day === 6) d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10); // YYYY-MM-DD
    };

    const isWeekend = () => {
        const day = new Date().getDay();
        return day === 0 || day === 6;
    };

    const handleOpenSnapshotDialog = () => setSnapshotDialogOpen(true);
    const handleCloseSnapshotDialog = () => {
        if (!snapshotting) setSnapshotDialogOpen(false);
    };

    const handleConfirmSnapshot = async () => {
        try {
            setSnapshotting(true);
            await takeDailyPnlSnapshot({
                date: snapshotDateStr(),
                data: tableData.map(r => {
                    const d = (v: number) => Number(formatNumber(v).replace(/,/g, ""));
                    return {
                        book: r.book,
                        strategy: r.strategy,
                        daily_change: d(r.daily_change),
                        cost: d(r.cost),
                        optimisation_margin: d(r.optimisation_margin),
                        pnl: d(r.pnl),
                        markup_pnl: d(r.markup_pnl),
                        commercial_pnl: d(r.commercial_pnl),
                        jv_total_pnl: d(r.jv_total_pnl),
                        jv_partner_pnl: d(r.jv_partner_pnl),
                        cgm_pnl: d(r.cgm_pnl),
                        net_pnl: d(r.net_pnl),
                    };
                }),
            });
            toast.success("Daily P&L snapshot saved");
            setSnapshotDialogOpen(false);
            setSnapshotRefreshKey(prev => prev + 1);
        } catch (err) {
            console.error(err);
            toast.error("Failed to save snapshot");
        } finally {
            setSnapshotting(false);
        }
    };

    const handleUpdateDailyChange = async () => {
        try {
            setUpdating(true);
            setError(null);
            await updateDailyChangeCloud2();
            await loadData();
            toast.success("Daily change updated successfully");
        } catch (err) {
            console.error(err);
            setError("Failed to update daily change");
            toast.error("Failed to update daily change");
        } finally {
            setUpdating(false);
        }
    };

    const columns = useMemo<MRT_ColumnDef<PnlDisplayRow>[]>(
        () => [
            {
                accessorKey: "book",
                header: "Book",
                size: 130,
                Cell: ({ row, cell }) =>
                    row.original.rowType !== "group" ? (
                        <span style={{ color: "#9e9e9e" }}></span>
                    ) : (
                        <span>{cell.getValue<string>()}</span>
                    ),
            },
            {
                accessorKey: "strategy",
                header: "Strategy",
                size: 200,
                Cell: ({ row, cell }) => {
                    const rowType = row.original.rowType;
                    const isSubRow = rowType !== "group";
                    return (
                        <span
                            style={{
                                paddingLeft: isSubRow ? 16 : 0,
                                fontStyle: isSubRow ? "italic" : "normal",
                                color: rowType === "cost_adjustment" ? "#d32f2f" : "inherit",
                                fontWeight: rowType === "adjusted" || rowType === "group" ? 700 : 400,
                            }}
                        >
                            {cell.getValue<string>()}
                        </span>
                    );
                },
            },
            {
                accessorKey: "pnl",
                header: "Gross P&L",
                size: 130,
                Cell: NumericCell,
            },
            {
                accessorKey: "daily_change",
                header: "Gross Daily Change",
                size: 200,
                Cell: ({ row, cell }) =>
                    row.original.rowType !== "group" ? (
                        <span style={{ color: "#9e9e9e" }}>—</span>
                    ) : (
                        <NumericCell cell={cell} />
                    ),
            },
            {
                accessorKey: "markup_pnl",
                header: "Markup P&L",
                size: 150,
                Cell: NumericCell,
            },
            {
                accessorKey: "optimisation_margin",
                header: "Optimisation P&L",
                size: 170,
                Cell: NumericCell,
            },
            {
                accessorKey: "commercial_pnl",
                header: "Commercial P&L",
                size: 160,
                Cell: NumericCell,
            },
            {
                accessorKey: "jv_total_pnl",
                header: "JV Total P&L",
                size: 150,
                Cell: NumericCell,
            },
            {
                accessorKey: "jv_partner_pnl",
                header: "JV Partner P&L",
                size: 160,
                Cell: NumericCell,
            },
            {
                accessorKey: "cgm_pnl",
                header: "CGM P&L",
                size: 130,
                Cell: NumericCell,
            },
            {
                accessorKey: "cost",
                header: "Costs",
                size: 130,
                Cell: NumericCell,
            },
            {
                accessorKey: "net_pnl",
                header: "Net P&L",
                size: 140,
                Cell: NumericCell,
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data: tableData,
        getSubRows: (row) => row.subRows,
        enableExpanding: true,
        enableExpandAll: true,
        filterFromLeafRows: true,
        displayColumnDefOptions: {
            "mrt-row-expand": {
                size: 36,
                header: "",
                muiTableHeadCellProps: {
                    sx: { padding: "4px 8px" },
                },
                muiTableBodyCellProps: {
                    sx: { padding: "4px 8px" },
                },
            },
        },
        muiExpandButtonProps: () => ({
            sx: {
                width: "22px",
                height: "22px",
                padding: 0,
                "& svg": { fontSize: "1.1rem" },
            },
        }),
        enableColumnFilters: true,
        enableSorting: true,
        enablePagination: false,
        enablePinning: false,
        enableColumnPinning: false,
        enableColumnResizing: true,
        enableStickyHeader: true,
        enableRowActions: false,
        enableRowSelection: false,
        state: {
            isLoading: loading,
            showColumnFilters: true,
        },
        initialState: {
            showColumnFilters: true,
            density: "compact",
        },
        columnFilterDisplayMode: "subheader",
        layoutMode: "grid-no-grow",
        muiTableContainerProps: {
            ref: tableContainerRef,
            onScroll: e => {
                const scrollLeft = (e.target as HTMLDivElement).scrollLeft;
                if (totalsInnerRef.current) {
                    totalsInnerRef.current.style.transform = `translateX(-${scrollLeft}px)`;
                }
            },
            sx: {
                maxHeight: "400px",
                overflowX: "auto",
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                backgroundColor: "#ffffff",
            },
        },
        renderTopToolbar: ({ table }) => (
            <Box>
                <MRT_TopToolbar table={table} />
                <TotalsBar table={table} totals={totals} innerRef={totalsInnerRef} />
            </Box>
        ),
        renderToolbarInternalActions: ({ table }) => (
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={isWeekend() ? "Snapshots are disabled on weekends" : "Take P&L Snapshot"}>
                    <span>
                        <Button
                            onClick={handleOpenSnapshotDialog}
                            disabled={snapshotting || tableData.length === 0 || isWeekend()}
                            size="small"
                            variant="outlined"
                            startIcon={<PhotoCameraIcon fontSize="small" />}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                borderRadius: "6px",
                                borderColor: "#c9d4dd",
                                color: "#fff !important",
                                marginRight: 5,
                                backgroundColor: "#2e7d32",
                                padding: "3px 10px",
                                minWidth: "auto",
                                "&:hover": {
                                    backgroundColor: "#2e7d32",
                                    borderColor: "#90a4ae",
                                },
                            }}
                        >
                            Snapshot
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Update Daily Change">
                    <span>
                        <Button
                            onClick={handleUpdateDailyChange}
                            disabled={updating}
                            size="small"
                            variant="outlined"
                            startIcon={
                                updating ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : (
                                    <RefreshIcon fontSize="small" />
                                )
                            }
                            sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                borderRadius: "6px",
                                borderColor: "#c9d4dd",
                                color: "#fff !important",
                                marginRight: 5,
                                backgroundColor: "#585bcc",
                                padding: "3px 10px",
                                minWidth: "auto",
                                "&:hover": {
                                    backgroundColor: "#585bcc",
                                    borderColor: "#90a4ae",
                                },
                            }}
                        >
                            {updating ? "Refreshing..." : "Refresh"}
                        </Button>
                    </span>
                </Tooltip>
                <MRT_ToggleGlobalFilterButton table={table} />
                <MRT_ToggleFiltersButton table={table} />
                <MRT_ToggleDensePaddingButton table={table} />
                <MRT_ToggleFullScreenButton table={table} />
            </Box>
        ),
        muiTableHeadRowProps: {
            sx: {
                background: "white",
                "& .MuiTableCell-root": {
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "#2c3e50",
                    borderRight: "1px solid #dcdcdc",
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                fontSize: "0.7rem",
                padding: "4px 8px",
                borderBottom: "1px solid #f1f1f1",
                borderRight: "1px solid #f1f1f1",
                "&:hover": {
                    backgroundColor: "#fafafa",
                },
            },
        },
        muiBottomToolbarProps: {
            sx: {
                backgroundColor: "#fff",
                padding: "8px",
            },
            className: "bottom-toolbar-wrapper__new",
        },
        muiTopToolbarProps: {
            sx: {
                backgroundColor: "#fff",
                padding: "8px",
            },
            className: "top-toolbar-wrapper__new",
        },
        muiPaginationProps: {
            shape: "rounded",
            size: "small",
            sx: {
                fontSize: "0.7rem",
                "& .MuiButtonBase-root": {
                    fontSize: "0.7rem",
                    minWidth: "28px",
                    height: "28px",
                },
            },
        },
        muiToolbarAlertBannerProps: {
            sx: {
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                fontSize: "0.75rem",
                borderRadius: 1,
            },
        },
    });

    return (
        <div className="page-body">
            <div className="table-ad__wrapper typeCapitalize">
                <Accordion
                    defaultActiveKey="0"
                    className="table-ad__addInputs"
                    style={{ width: "100%" }}
                >
                    <Accordion.Item eventKey="0">
                        <Accordion.Header
                            className="accordion_button"
                            style={{
                                fontSize: "0.8rem",
                                backgroundColor: "#d1edf1",
                                padding: "6px 12px",
                                borderRadius: "4px",
                                fontWeight: 600,
                            }}
                        >
                            P&L Summary View-3
                        </Accordion.Header>

                        <Accordion.Body>
                            {error && (
                                <div
                                    style={{
                                        color: "#d32f2f",
                                        fontSize: "0.8rem",
                                        marginBottom: 8,
                                    }}
                                >
                                    {error}
                                </div>
                            )}
                            <Box sx={{ display: "flex", gap: 2, mb: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
                                <Box sx={{ display: "flex", gap: 2, maxWidth: 500, flex: 1, minWidth: 260 }}>
                                    <FilterField
                                        label="Book"
                                        field="book"
                                        options={bookOptions}
                                        value={draftFilters.book}
                                        onChange={handleFilterChange}
                                    />
                                    <FilterField
                                        label="Strategy"
                                        field="strategy"
                                        options={strategyOptions}
                                        value={draftFilters.strategy}
                                        onChange={handleFilterChange}
                                    />
                                </Box>

                                <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                                    <ActionButton
                                        label="Apply"
                                        onClick={handleApplyFilters}
                                        backgroundColor="#00bcd4"
                                        disabled={isApplyDisabled}
                                    />
                                    <ActionButton
                                        label="Clear All"
                                        onClick={handleClearAllFilters}
                                        backgroundColor="#f44336"
                                        disabled={isClearAllDisabled}
                                    />
                                </Box>
                            </Box>

                            <div className="table-ad__wrapperCard">
                                <MaterialReactTable table={table} />
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
                <PnlSnapshotHistoryTable refreshKey={snapshotRefreshKey}/>
                <Dialog
                    open={snapshotDialogOpen}
                    onClose={handleCloseSnapshotDialog}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#2c3e50", pb: 1 }}>
                        Confirm Daily P&L Snapshot
                    </DialogTitle>
                    <DialogContent sx={{ pt: 0 }}>
                        <DialogContentText sx={{ fontSize: "0.8rem" }}>
                            Save P&L snapshot for <b>{snapshotDateStr()}</b>? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            onClick={handleCloseSnapshotDialog}
                            disabled={snapshotting}
                            size="small"
                            sx={{ textTransform: "none", fontSize: "0.75rem", color: "#616161" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmSnapshot}
                            disabled={snapshotting}
                            size="small"
                            variant="contained"
                            startIcon={snapshotting ? <CircularProgress size={14} color="inherit" /> : <PhotoCameraIcon fontSize="small" />}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                backgroundColor: "#2e7d32",
                                "&:hover": { backgroundColor: "#256428" },
                            }}
                        >
                            {snapshotting ? "Saving..." : "Confirm"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
};

export default PnlSummaryView3;